import React from 'react';
import {DatePicker, DatePickerProps} from '@material-ui/pickers/DatePicker';
import MuiPickersUtilsProvider from '@material-ui/pickers/MuiPickersUtilsProvider';
import {createStyles, withStyles, MuiThemeProvider} from '@material-ui/core/styles';
import DateUtils from '@date-io/date-fns';
import ruLocale from 'date-fns/locale/ru';
import format from 'date-fns/format';
import {BaseFieldProps, BaseFieldVariant} from '../../types';
import {images} from '../../../../../assets/icons';
import {useIsDesktop} from '../../../../hooks/useIsDesktop';
import {useTheme} from '../../../../theme';
import {Theme} from '../../../../theme/interface';
import {dateFormats} from '../../../../constants/date';

class LocalizedDateUtils extends DateUtils {
  getCalendarHeaderText(date: Date) {
    return format(date, 'LLLL yyyy', {locale: this.locale});
  }
}

export const BaseFieldDate: React.FC<BaseFieldProps> = (props) => {
  const {label, input, readOnly, variant} = props;
  const {minDate, maxDate, disableToolbar = true} = props as unknown as DatePickerProps;

  const theme = useTheme();
  const isDesktop = useIsDesktop();

  const StyledDatePicker = React.useMemo(
    () => withStyles(createCustomStyles(theme, variant!, isDesktop, readOnly))(DatePicker),
    [variant, isDesktop, readOnly],
  );

  return (
    <MuiPickersUtilsProvider utils={LocalizedDateUtils} locale={ruLocale}>
      <MuiThemeProvider theme={theme.muiDefaultTheme}>
        {/* @ts-expect-error withStyles type inference issue */}
        <StyledDatePicker
          format={dateFormats.inputDate}
          // cancelLabel='ОТМЕНА'
          // okLabel='ОК'
          invalidDateMessage='Недопустимый формат даты'
          minDateMessage='Дата не должна быть меньше минимальной даты'
          maxDateMessage='Дата не должна быть больше максимальной даты'
          variant='inline'
          onChange={(event) => input.onChange && input.onChange(event)}
          value={input.value}
          InputProps={{
            endAdornment: <images.Calendar color={theme.input.variants.outlined.borderColor} width={30} height={30} />,
          }}
          disableToolbar={disableToolbar}
          inputVariant={variant}
          readOnly={readOnly}
          label={label}
          minDate={minDate}
          maxDate={maxDate}
          autoOk
        />
      </MuiThemeProvider>
    </MuiPickersUtilsProvider>
  );
};

const createCustomStyles =
  (theme: Theme, variant: BaseFieldVariant, isDesktop: boolean, readOnly = false) =>
  () => {
    if (variant === 'outlined') {
      return createStyles({
        root: {
          '& .MuiOutlinedInput-input': {
            ...theme.fonts.body,
            lineHeight: isDesktop ? '28px' : '20px',
            padding: isDesktop ? '8px 10px' : '8px 6px',
            color: theme.input.variants.outlined.borderColor,
          },
          '& .MuiInputLabel-outlined:not(.Mui-focused):not(.MuiFormLabel-filled)': {
            transform: 'translate(14px, 14px) scale(1)',
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderWidth: '1px !important',
              borderColor: theme.input.variants.outlined.borderColor,
            },
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.color.primaryColor,
          },
          '& .MuiFormLabel-root.Mui-focused': {
            color: theme.color.primaryColor,
          },
          ...(readOnly
            ? {
                '& .Mui-disabled': {
                  color: theme.color.textColor,
                },
              }
            : {}),
        },
      });
    }

    return createStyles({
      root: {
        '& .MuiInputBase-root': {
          color: theme.color.textColor,
        },
        '& .MuiInput-underline:before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
      },
    });
  };
